/**
 * Shared Emotion styled primitives for Vellum's page-level UI.
 * All values reference Codex CSS variables emitted by MuiCssBaseline.
 */
import styled from '@emotion/styled';

// ── Layout ────────────────────────────────────────────────────────────────────

export const PageWrapper = styled.div<{ $maxWidth?: string | number }>`
  max-width: ${({ $maxWidth }) =>
    typeof $maxWidth === 'number' ? `${$maxWidth}px` : ($maxWidth ?? '800px')};
  margin: 0 auto;
  padding: var(--spacing-6) 16px var(--spacing-10);

  @media (min-width: 640px) {
    padding: var(--spacing-10) 16px var(--spacing-16);
  }
`;

export const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--spacing-8);
  gap: var(--spacing-4);
  flex-wrap: wrap;
`;

export const PageTitle = styled.h1<{ $mb?: string }>`
  font-family: var(--font-heading);
  font-size: 24px;
  font-weight: var(--font-weight-bold);
  color: var(--color-text-primary);
  margin: 0 0 ${({ $mb }) => $mb ?? '0'};
  letter-spacing: -0.01em;
`;

// ── Cards ─────────────────────────────────────────────────────────────────────

export const Card = styled.div<{ $mb?: string }>`
  background: var(--color-bg-elevated);
  border: 1px solid var(--color-border-default);
  border-radius: var(--radius-md);
  padding: 20px var(--spacing-6);
  ${({ $mb }) => ($mb ? `margin-bottom: ${$mb};` : '')}
`;

export const CardTitle = styled.h2<{ $mb?: string }>`
  font-family: var(--font-heading);
  font-size: 16px;
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
  margin: 0 0 ${({ $mb }) => $mb ?? 'var(--spacing-4)'};
`;

// ── Stats grid (creator dashboard) ───────────────────────────────────────────

export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: var(--spacing-4);
  margin-bottom: var(--spacing-10);
`;

export const StatCard = styled.div`
  background: var(--color-bg-elevated);
  border: 1px solid var(--color-border-default);
  border-radius: var(--radius-md);
  padding: 20px var(--spacing-6);
`;

export const StatValue = styled.div`
  font-family: var(--font-heading);
  font-size: 28px;
  font-weight: var(--font-weight-bold);
  color: var(--color-text-primary);
  margin-bottom: 4px;
  letter-spacing: -0.02em;
`;

export const StatLabel = styled.div`
  font-size: 13px;
  color: var(--color-text-disabled);
`;

// ── Section titles ────────────────────────────────────────────────────────────

export const SectionTitle = styled.h2`
  font-family: var(--font-heading);
  font-size: 18px;
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
  margin: 0;
`;

export const SectionRow = styled.div<{ $mb?: string }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ $mb }) => $mb ?? 'var(--spacing-4)'};
  gap: var(--spacing-3);
  flex-wrap: wrap;
`;

// ── Buttons — Use <Button> and <ButtonLink> from '@codex/components/inputs' ─────
// The Emotion button primitives (PrimaryBtn, OutlineBtn, DangerBtn, PrimaryBtnLink)
// were removed in favour of the full Codex Button component, which implements
// Material Design 3 with 5 variants, 5 sizes, toggle support, and shape morphing.
//
// Quick migration reference:
//   PrimaryBtn       → <Button variant="filled" size="sm">
//   OutlineBtn       → <Button variant="outlined" size="sm">
//   DangerBtn        → <Button variant="outlined" size="sm" color="error">
//   PrimaryBtnLink   → <ButtonLink variant="filled" size="sm" to="...">

// ── Form elements ─────────────────────────────────────────────────────────────

export const FormGrid = styled.form`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-4);
`;

export const FormRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const FormLabel = styled.label`
  font-size: 13px;
  font-weight: var(--font-weight-medium);
  color: var(--color-text-primary);
`;

export const FormInput = styled.input`
  padding: 9px 12px;
  border: 1px solid var(--color-border-default);
  border-radius: var(--radius-md);
  font-size: 14px;
  font-family: var(--font-body);
  color: var(--color-text-primary);
  background: var(--color-bg-elevated);
  width: 100%;
  box-sizing: border-box;
  transition: border-color var(--duration-shortest) ease;

  &:focus {
    outline: none;
    border-color: var(--color-border-focus);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--color-border-focus) 15%, transparent);
  }
  &::placeholder {
    color: var(--color-text-placeholder);
  }
`;

export const FormTextarea = styled(FormInput.withComponent('textarea'))`
  resize: vertical;
  min-height: 72px;
`;

// ── Tabs ──────────────────────────────────────────────────────────────────────

export const TabBar = styled.div<{ $mb?: string }>`
  display: flex;
  gap: 4px;
  border-bottom: 1px solid var(--color-border-default);
  margin-bottom: ${({ $mb }) => $mb ?? 'var(--spacing-8)'};

  /* Horizontal scroll so tab strips don't clip on narrow viewports.
     Hide the scrollbar — the active underline already communicates
     which tab is selected, and swipe scrolling is expected on mobile. */
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

export const Tab = styled.button<{ active?: boolean }>`
  padding: 8px 18px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: var(--font-weight-medium);
  font-family: var(--font-body);
  color: ${({ active }) => (active ? 'var(--color-text-primary)' : 'var(--color-text-disabled)')};
  border-bottom: 2px solid ${({ active }) => (active ? 'var(--color-primary)' : 'transparent')};
  margin-bottom: -1px;
  transition: color var(--duration-shortest) ease;
  white-space: nowrap;
  flex-shrink: 0;
  &:hover {
    color: var(--color-text-primary);
  }
`;

// ── Item rows ─────────────────────────────────────────────────────────────────

export const ItemRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
  background: var(--color-bg-elevated);
  border: 1px solid var(--color-border-default);
  border-radius: var(--radius-md);
  flex-wrap: wrap;
`;

export const ItemName = styled.div`
  font-size: 15px;
  font-weight: var(--font-weight-medium);
  color: var(--color-text-primary);
`;

export const ItemMeta = styled.div<{ $color?: string; $mt?: string; $fw?: number | string }>`
  font-size: 13px;
  color: ${({ $color }) => $color ?? 'var(--color-text-disabled)'};
  margin-top: ${({ $mt }) => $mt ?? '2px'};
  ${({ $fw }) => ($fw ? `font-weight: ${$fw};` : '')}
`;

export const ItemActions = styled.div`
  display: flex;
  gap: var(--spacing-2);
  flex-shrink: 0;
`;

// ── Empty state ───────────────────────────────────────────────────────────────

// ── Skeleton / shimmer loading ────────────────────────────────────────────────

export const SkeletonRow = styled.div<{ $gap?: string }>`
  display: flex;
  align-items: center;
  gap: ${({ $gap }) => $gap ?? 'var(--spacing-3)'};
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: var(--spacing-16) 0;
  color: var(--color-text-disabled);

  .icon {
    font-size: 40px;
    margin-bottom: var(--spacing-4);
  }
  p {
    font-size: 16px;
    margin: 0;
  }
  small {
    font-size: 14px;
    margin-top: 8px;
    display: block;
  }
`;

// ── Loading state ─────────────────────────────────────────────────────────────

export const LoadingState = styled.div<{ $size?: string }>`
  padding: 48px;
  text-align: center;
  color: var(--color-text-disabled);
  font-size: ${({ $size }) => $size ?? '14px'};
`;

// ── Split rows ────────────────────────────────────────────────────────────────

export const KeyValueRow = styled.div<{ $noBorder?: boolean }>`
  display: flex;
  justify-content: space-between;
  gap: var(--spacing-3);
  padding: 8px 0;
  border-bottom: ${({ $noBorder }) =>
    $noBorder ? 'none' : '1px solid var(--color-border-subtle)'};
  font-size: 14px;
  flex-wrap: wrap;

  &:last-child {
    border-bottom: none;
  }
`;

export const KeyLabel = styled.span`
  color: var(--color-text-disabled);
`;
export const KeyValue = styled.span<{ $color?: string }>`
  color: ${({ $color }) => $color ?? 'var(--color-text-primary)'};
  font-weight: var(--font-weight-medium);
`;

// ── Chips / badges ────────────────────────────────────────────────────────────

export const Chip = styled.span<{ color?: string }>`
  display: inline-block;
  font-size: 11px;
  font-weight: var(--font-weight-semibold);
  padding: 2px 8px;
  border-radius: var(--radius-full);
  background: ${({ color }) =>
    color ? `color-mix(in srgb, ${color} 15%, transparent)` : 'var(--color-bg-hover)'};
  color: ${({ color }) => color ?? 'var(--color-text-secondary)'};
  border: 1px solid
    ${({ color }) =>
      color ? `color-mix(in srgb, ${color} 25%, transparent)` : 'var(--color-border-default)'};
`;

// ── Inline error / success feedback ──────────────────────────────────────────

export const InlineError = styled.span`
  color: var(--color-text-error);
  font-size: 13px;
`;

export const InlineSuccess = styled.span`
  color: var(--color-text-success);
  font-weight: var(--font-weight-medium);
`;

// ── Skeleton loaders ──────────────────────────────────────────────────────────

export const SkeletonBlock = styled.div<{
  $h?: string;
  $w?: string;
  $mb?: string;
  $radius?: string;
}>`
  height: ${({ $h }) => $h ?? '16px'};
  width: ${({ $w }) => $w ?? '100%'};
  margin-bottom: ${({ $mb }) => $mb ?? '0'};
  border-radius: ${({ $radius }) => $radius ?? 'var(--radius-sm)'};
  background: linear-gradient(
    90deg,
    var(--color-bg-elevated) 25%,
    var(--color-bg-hover) 50%,
    var(--color-bg-elevated) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  @keyframes shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

export const SkeletonAvatar = styled(SkeletonBlock)<{ $size?: string }>`
  width: ${({ $size }) => $size ?? '40px'};
  height: ${({ $size }) => $size ?? '40px'};
  border-radius: 50%;
  flex-shrink: 0;
`;

export const SkeletonWrap = styled.div`
  padding: var(--spacing-6) var(--spacing-4);
  max-width: 960px;
  margin: 0 auto;

  @media (min-width: 768px) {
    padding: var(--spacing-8);
  }
`;

export const SkeletonStatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: var(--spacing-4);
  margin-bottom: var(--spacing-8);
`;

// ── Stack layout ──────────────────────────────────────────────────────────────

export const Stack = styled.div<{ $gap?: string; $direction?: string }>`
  display: flex;
  flex-direction: ${({ $direction }) => $direction ?? 'column'};
  gap: ${({ $gap }) => $gap ?? 'var(--spacing-4)'};
`;
